import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { testcaseApi } from "../../../api/testcase";
import { workflowApi } from "../../../api/workflow";
import { apicallApi } from "../../../api/apicall";
import Navbar from "../../layout/Navbar";
// DragDropContext, Droppable, Draggable removed - using SavedSteps component instead
import apiEndpoints from "../../../api/endpoints";
import ScreenshotPreview from "../../sections/ScreenshotPreview";
import DataFixture from "../../sections/DataFixture";
import TestStepForm from "../../sections/TestStepForm";
import SavedSteps from "../../sections/SavedSteps";
import GeneratedSteps from "../../sections/GeneratedSteps";
import { v4 as uuidv4 } from "uuid";

const TEST_ACTIONS = [
  {
    value: "type",
    label: "Type Text",
    fields: [
      {
        name: "value",
        label: "Text to type",
        placeholder: "Enter text to type",
      },
      {
        name: "element",
        label: "Element to type into",
        placeholder: "Type name of input here",
      },
    ],
  },
  {
    value: "click",
    label: "Click Element",
    fields: [
      {
        name: "element",
        label: "Element to click",
        placeholder: "Type element to click",
      },
    ],
  },
  {
    value: "navigate",
    label: "Navigate to URL",
    fields: [
      { name: "value", label: "URL", placeholder: "Enter URL to navigate to" },
    ],
  },
  {
    value: "select",
    label: "Select Option",
    fields: [
      {
        name: "value",
        label: "Option to Select",
        placeholder: "Enter option text or value",
      },
      {
        name: "element",
        label: "Dropdown Element",
        placeholder: "Type name of dropdown",
      },
    ],
  },
  {
    value: "workflow",
    label: "Use Workflow",
    fields: [],
  },
  {
    value: "assert",
    label: "Add Assertion",
    fields: [
      { name: "value", label: "Assertion", placeholder: "Enter assertion text" }
    ]
  },
  {
    value: "wait",
    label: "Wait",
    fields: [
      {
        name: "value",
        label: "Wait time (seconds)",
        placeholder: "Enter wait time in seconds",
        type: "number"
      }
    ]
  },
  {
    value: "reload",
    label: "Reload Page",
    fields: [],
  }
];

function EditTestCase() {
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    testname: "",
    currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
    savedSteps: [],
    assertion: "",
    description: "",
    is_disabled: false,
  });
  
  // Store the original values for comparison
  const [originalData, setOriginalData] = useState({
    testname: "",
    savedSteps: [],
    savedApiCalls: [],
    is_disabled: false
  });

  const [isLoading, setIsLoading] = useState(true);
  const [availableWorkflows, setAvailableWorkflows] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [screenshots, setScreenshots] = useState([]);
  const [initialScreenshot, setInitialScreenshot] = useState(null);

  const [testStatus, setTestStatus] = useState(null);
  const pollIntervalRef = useRef(null);
  const [suiteId, setSuiteId] = useState(null); // Add this
  const [genratedAssertions, setGeneratedAssertions] = useState([]);
  const [isAssertionGenerating, setIsAssertionGenerating] = useState(false);
  const [isFixtureModalOpen, setIsFixtureModalOpen] = useState(false);
  const [pendingSteps, setPendingSteps] = useState([]);
  const [showGeneratedSteps, setShowGeneratedSteps] = useState(false);
  const [isGeneratingSteps, setIsGeneratingSteps] = useState(false);

  // Add state for API calls
  const [apiCallMode, setApiCallMode] = useState(false);
  const [savedApiCalls, setSavedApiCalls] = useState([]);
  const [editingApiCallIndex, setEditingApiCallIndex] = useState(undefined);
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [apiCall, setApiCall] = useState({
    name: '',
    method: 'GET',
    url: '',
    body: '',
    position: 'before'
  });
  
  // Add state for existing API calls
  const [availableApiCalls, setAvailableApiCalls] = useState([]);
  const [selectedApiCallId, setSelectedApiCallId] = useState('');
  const [isLoadingApiCalls, setIsLoadingApiCalls] = useState(false);
  const [apiCallsError, setApiCallsError] = useState(null);

  // Add state for step screenshots
  const [stepScreenshot, setStepScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [isUploadingScreenshot, setIsUploadingScreenshot] = useState(false);

  const { testCaseId } = useParams();
  const navigate = useNavigate();

  // Fetch existing test case data
  useEffect(() => {
    const fetchTestCase = async () => {
      try {
        setIsLoading(true);
        const testCase = await testcaseApi.getTestCaseById(testCaseId);

        // Separate API call steps from regular steps
        const regularSteps = [];
        const apiCallSteps = [];

        testCase.steps.forEach((step) => {
          if (step.metadata?.actionType === 'apicall') {
            // This is an API call step
            apiCallSteps.push({
              id: step.metadata.endpoint_id,
              name: step.name.replace('API Call: ', ''),
              order: step.order,
              // We'll fetch the full API call details later
            });
          } else {
            // This is a regular step
                          const parsedStep = {
               id: step.id,
               order: step.order,
               action: step.metadata?.actionType || "",
               value: step.metadata?.actionType === "assert" 
                 ? (step.metadata?.assertion || "") 
                 : (step.metadata?.value || ""),
               element: step.metadata?.selector || "",
               name: step.name,
               exact: step.metadata?.exact || false,
               secret: step.metadata?.secret || false,
               value_mode: step.metadata?.value_mode || "static",
               ...(step.metadata?.actionType === "assert" && { assertion_mode: step.metadata?.assertion_mode || 'flexible' }),
               metadata: step.metadata || {}
             };

       
            // Handle workflow steps
            if (step.type === "workflow" && step.workflow_id) {
              parsedStep.action = "workflow";
              parsedStep.workflowId = step.workflow_id;
              parsedStep.workflowName = step.metadata?.workflow_name || "Unknown workflow";
            }

            regularSteps.push(parsedStep);
          }
        });

        // Set regular steps
        setFormData({
          testname: testCase.testname,
          currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
          savedSteps: regularSteps,
          assertion: testCase.assertion || "",
          description: testCase.description || "",
          is_disabled: testCase.is_disabled || false,
        });

        // Store original values for change detection
        setOriginalData({
          testname: testCase.testname,
          savedSteps: regularSteps,
          savedApiCalls: [],  // Will be updated after API calls are fetched
          is_disabled: testCase.is_disabled || false
        });

        // Fetch full API call details for each API call step
        const apiCallPromises = apiCallSteps.map(async (step) => {
          try {
            const apiCall = await apicallApi.getApiCallById(step.id);
            return {
              id: apiCall.id,
              name: apiCall.name,
              method: apiCall.method,
              url: apiCall.url,
              order: step.order
            };
          } catch (error) {
            console.error(`Failed to fetch API call details for ID ${step.id}:`, error);
            // Return basic info we have from the test case
            return {
              id: step.id,
              name: step.name,
              method: 'Unknown',
              url: 'Unknown',
              order: step.order
            };
          }
        });

        const resolvedApiCalls = await Promise.all(apiCallPromises);
        setSavedApiCalls(resolvedApiCalls);
        
        // Update original data with API calls
        setOriginalData(prev => ({
          ...prev,
          savedApiCalls: resolvedApiCalls
        }));

        setSuiteId(testCase.test_suite_id);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching test case:", error);
        setError("Failed to load test case");
        setIsLoading(false);
      }
    };

    fetchTestCase();
  }, [testCaseId]);

  // Add useEffect to fetch workflows
  useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        if (suiteId) {

          const workflows = await workflowApi.getWorkflowsBySuite(suiteId);
          setAvailableWorkflows(workflows || []);
        }
      } catch (error) {
        console.error("Failed to fetch workflows:", error);
        setError("Failed to load available workflows");
      }
    };

    fetchWorkflows();
  }, [suiteId]);

  // Add useEffect to fetch available API calls
  useEffect(() => {
    const fetchAvailableApiCalls = async () => {
      try {
        setIsLoadingApiCalls(true);
        setApiCallsError(null);
        
        // Use the apicallApi service to fetch all available API calls
        const apiCalls = await apicallApi.getAllApiCalls();
        setAvailableApiCalls(apiCalls);
        
        setIsLoadingApiCalls(false);
      } catch (error) {
        console.error('Failed to fetch API calls:', error);
        setApiCallsError('Failed to load available API calls');
        setIsLoadingApiCalls(false);
      }
    };

    fetchAvailableApiCalls();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Handler for the password checkbox
  const handleToggleSecret = () => {
    setFormData(prev => ({
      ...prev,
      currentStep: {
        ...prev.currentStep,
        secret: !prev.currentStep.secret
      }
    }));
  };

  const uploadScreenshotToSupabase = async (screenshotBlob, stepId) => {
    if (!screenshotBlob) return null;
    
    try {
      setIsUploadingScreenshot(true);
      
      // Use a placeholder ID for upload if stepId is null - this won't be stored in the step record
      const uploadId = stepId || `upload-${Date.now()}`;
      
      // Use the centralized API endpoint instead of direct fetch
      const result = await apiEndpoints.uploadStepScreenshot(screenshotBlob, uploadId);
      
      if (result && result.url) {
        console.log('📸 Upload successful:', result.url);
        return result.url;
      } else {
        throw new Error('Upload response missing URL');
      }
      
    } catch (error) {
      console.error('📸 Error uploading screenshot:', error);
      setError(`Failed to upload screenshot: ${error.message}`);
      return null;
    } finally {
      setIsUploadingScreenshot(false);
    }
  };

  // Add this function to handle adding new steps
  const handleSaveStep = async () => {
    const { action, value, element, workflowId, exact, secret, value_mode } = formData.currentStep;

    console.log('im here')

    // For click actions, respect the exact flag but don't modify the text
    const exactMatch = action === 'click' ? exact : false;
    
    // Calculate the next order number
    const nextOrder = formData.savedSteps.length + 1;

    // Handle different action types
    if (action === "workflow") {
      if (!workflowId) {
        setError("Please select a workflow");
        return;
      }

      // Find the selected workflow to get its name
      const selectedWorkflow = availableWorkflows.find(
        (w) => w.id === workflowId
      );
      if (!selectedWorkflow) {
        setError("Selected workflow not found");
        return;
      }

      // Check if we're editing an existing step
      if (formData.editingStepIndex !== undefined) {
        setFormData((prevState) => {
          const updatedSteps = [...prevState.savedSteps];
          updatedSteps[prevState.editingStepIndex] = {
            ...updatedSteps[prevState.editingStepIndex], // Preserve existing properties like id
            action: "workflow",
            workflowId,
            workflowName: selectedWorkflow.name,
          };

          return {
            ...prevState,
            savedSteps: updatedSteps,
            currentStep: {
              action: "type", 
              value: "", 
              element: "", 
              workflowId: "", 
              exact: false,
              secret: false,
              value_mode: "static"
            },
            editingStepIndex: undefined, // Clear editing state
          };
        });
      } else {
        // Add new workflow step
        setFormData((prevState) => ({
          ...prevState,
          savedSteps: [
            ...prevState.savedSteps,
            {
              id: uuidv4(),
              action: "workflow",
              workflowId,
              workflowName: selectedWorkflow.name,
              order: nextOrder,
            },
          ],
          currentStep: {
            action: "type",
            value: "",
            element: "",
            workflowId: "",
            exact: false,
            secret: false,
            value_mode: "static"
          },
        }));
      }
      
      setStepScreenshot(null);
      setScreenshotPreview(null);
      
      setError(null);
      return;
    }

    // Special handling for "enter" action which doesn't need value or element
    if (action === "enter") {
      if (formData.editingStepIndex !== undefined) {
        setFormData((prevState) => {
          const updatedSteps = [...prevState.savedSteps];
          updatedSteps[prevState.editingStepIndex] = {
            ...updatedSteps[prevState.editingStepIndex], // Preserve existing properties like id
            action: "enter",
            value: "",
            element: "",
            secret: false
          };

          return {
            ...prevState,
            savedSteps: updatedSteps,
            currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
            editingStepIndex: undefined,
          };
        });
      } else {
        setFormData((prevState) => ({
          ...prevState,
          savedSteps: [
            ...prevState.savedSteps,
            {
              id: uuidv4(),
              action: "enter",
              value: "",
              element: "",
              order: nextOrder,
              secret: false
            },
          ],
          currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" }, // Reset to default
        }));
      }
      
      setStepScreenshot(null);
      setScreenshotPreview(null);
      
      return;
    }

    // For navigate, only URL is required
    if (action === "navigate") {
      if (!value) {
        setError("Please enter a URL");
        return;
      }

      // Handle screenshot URL for navigate actions
      let screenshotUrl = null;
      if (stepScreenshot) {
        console.log('hellooooo')
        const stepId = formData.editingStepIndex !== undefined 
          ? formData.savedSteps[formData.editingStepIndex].id || uuidv4()
          : uuidv4();
        
        if (stepScreenshot.uploadedUrl) {
          screenshotUrl = stepScreenshot.uploadedUrl;
        } else {
          screenshotUrl = await uploadScreenshotToSupabase(stepScreenshot, stepId);
        }
      }

      if (formData.editingStepIndex !== undefined) {
        console.log('hellooooo 2')
        setFormData((prevState) => {
          const updatedSteps = [...prevState.savedSteps];
          updatedSteps[prevState.editingStepIndex] = {
            ...updatedSteps[prevState.editingStepIndex], // Preserve existing properties like id
            action,
            value,
            element: "",
            secret: false, // Not applicable for navigate
            value_mode: value_mode || "static", // Include value_mode for navigate
            metadata: {
              ...updatedSteps[prevState.editingStepIndex].metadata,
              actionType: action,
              selector: "",
              value: value,
              value_mode: value_mode || "static",
              ...(screenshotUrl && { step_screenshot: screenshotUrl })
            }
          };

          return {
            ...prevState,
            savedSteps: updatedSteps,
            currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
            editingStepIndex: undefined,
          };
        });
      } else {
        setFormData((prevState) => ({
          ...prevState,
          savedSteps: [
            ...prevState.savedSteps,
            {
              id: uuidv4(),
              action,
              value,
              element: "",
              order: nextOrder,
              secret: false, // Not applicable for navigate
              value_mode: value_mode || "static", // Include value_mode for navigate
              metadata: {
                actionType: action,
                selector: "",
                value: value,
                value_mode: value_mode || "static",
                ...(screenshotUrl && { step_screenshot: screenshotUrl })
              }
            },
          ],
          currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
        }));
      }
      
      // Clear screenshot state after saving navigate step
      setStepScreenshot(null);
      setScreenshotPreview(null);
      
      return;
    }

    // For click, only element is required
    if (action === "click") {
      if (!element) {
        setError("Please specify an element to click");
        return;
      }

      // Handle screenshot URL for click actions
      let screenshotUrl = null;
      if (stepScreenshot) {
        const stepId = formData.editingStepIndex !== undefined 
          ? formData.savedSteps[formData.editingStepIndex].id || uuidv4()
          : uuidv4();
        
        if (stepScreenshot.uploadedUrl) {
          screenshotUrl = stepScreenshot.uploadedUrl;
        } else {
          screenshotUrl = await uploadScreenshotToSupabase(stepScreenshot, stepId);
        }
      }

      if (formData.editingStepIndex !== undefined) {
        setFormData((prevState) => {
          const updatedSteps = [...prevState.savedSteps];
          updatedSteps[prevState.editingStepIndex] = {
            ...updatedSteps[prevState.editingStepIndex], // Preserve existing properties like id
            action,
            value: "",
            element: element || '',
            exact: exactMatch,
            secret: false, // Not applicable for click
            metadata: {
              ...updatedSteps[prevState.editingStepIndex].metadata,
              actionType: action,
              selector: element,
              value: "",
              exact: exactMatch,
              ...(screenshotUrl && { step_screenshot: screenshotUrl })
            }
          };

          return {
            ...prevState,
            savedSteps: updatedSteps,
            currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
            editingStepIndex: undefined,
          };
        });
      } else {
        setFormData((prevState) => ({
          ...prevState,
          savedSteps: [
            ...prevState.savedSteps,
            {
              id: uuidv4(),
              action,
              value: "",
              element: element || '',
              exact: exactMatch,
              order: nextOrder,
              secret: false, // Not applicable for click
              metadata: {
                actionType: action,
                selector: element,
                value: "",
                exact: exactMatch,
                ...(screenshotUrl && { step_screenshot: screenshotUrl })
              }
            },
          ],
          currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
        }));
      }
      
      // Clear screenshot state after saving click step
      setStepScreenshot(null);
      setScreenshotPreview(null);
      
      return;
    }

    // For type, only element is required, value can be empty
    if (action === "type") {
      if (!element) {
        setError("Please provide an element to type into");
        return;
      }
    }

    // For select, both value and element are required
    if (action === "select") {
      if (!value || !element) {
        setError("Please provide both option to select and dropdown element");
        return;
      }
    }

    // For assert, only value is required
    if (action === "assert") {
      if (!value) {
        setError("Please enter an assertion");
        return;
      }

      if (formData.editingStepIndex !== undefined) {
        setFormData((prevState) => {
          const updatedSteps = [...prevState.savedSteps];
                    updatedSteps[prevState.editingStepIndex] = {
             ...updatedSteps[prevState.editingStepIndex], // Preserve existing properties like id
             action,
             value,
             element: "",
             secret: false, // Not applicable for assert
             assertion_mode: formData.currentStep.assertion_mode || 'flexible',
             metadata: {
               actionType: action,
               assertion: value,
               assertion_mode: formData.currentStep.assertion_mode || 'flexible'
             }
           };

          return {
            ...prevState,
            savedSteps: updatedSteps,
            currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
            editingStepIndex: undefined,
          };
        });
      } else {
        setFormData((prevState) => ({
          ...prevState,
          savedSteps: [
            ...prevState.savedSteps,
            {
              id: uuidv4(),
              action,
              value,
              element: "",
              order: nextOrder,
              secret: false, // Not applicable for assert
              metadata: {
                actionType: action,
                assertion: value
              }
            },
          ],
          currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
        }));
      }
      return;
    }

    // For wait, only value (in seconds) is required
    if (action === "wait") {
      if (!value || parseFloat(value) <= 0) {
        setError("Wait time must be a positive number");
        return;
      }

      // Convert seconds to milliseconds for backend
      const waitTimeMs = Math.round(parseFloat(value) * 1000).toString();

      if (formData.editingStepIndex !== undefined) {
        setFormData((prevState) => {
          const updatedSteps = [...prevState.savedSteps];
          updatedSteps[prevState.editingStepIndex] = {
            ...updatedSteps[prevState.editingStepIndex], // Preserve existing properties like id
            action,
            value: waitTimeMs, // Store as milliseconds string
            element: "",
            secret: false // Not applicable for wait
          };

          return {
            ...prevState,
            savedSteps: updatedSteps,
            currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
            editingStepIndex: undefined,
          };
        });
      } else {
        setFormData((prevState) => ({
          ...prevState,
          savedSteps: [
            ...prevState.savedSteps,
            {
              id: uuidv4(),
              action,
              value: waitTimeMs, // Store as milliseconds string
              element: "",
              order: nextOrder,
              secret: false // Not applicable for wait
            },
          ],
          currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
        }));
      }
      return;
    }

    if (action === "reload") {
      if (formData.editingStepIndex !== undefined) {
        setFormData((prevState) => {
          const updatedSteps = [...prevState.savedSteps];
          updatedSteps[prevState.editingStepIndex] = {
            ...updatedSteps[prevState.editingStepIndex], 
            action,
            value: "",
            element: "",
            secret: false
          };

          return {
            ...prevState,
            savedSteps: updatedSteps,
            currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
            editingStepIndex: undefined,
          };
        });
      } else {
        setFormData((prevState) => ({
          ...prevState,
          savedSteps: [
            ...prevState.savedSteps,
            {
              id: uuidv4(),
              action,
              value: "",
              element: "",
              order: nextOrder,
              secret: false
            },
          ],
          currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
        }));
      }
      return;
    }

    // Default case for other actions
    if (formData.editingStepIndex !== undefined) {
      // Handle screenshot URL for editing existing steps
      let screenshotUrl = null;
      const stepId = formData.savedSteps[formData.editingStepIndex].id || uuidv4();
      
      if (stepScreenshot && (action === "type" || action === "click" || action === "select")) {
        // Check if screenshot already has an uploaded URL
        if (stepScreenshot.uploadedUrl) {
          screenshotUrl = stepScreenshot.uploadedUrl;
        } else {
          // Fallback: upload if not already uploaded
          screenshotUrl = await uploadScreenshotToSupabase(stepScreenshot, stepId);
        }
      }
      
      setFormData((prevState) => {
        const updatedSteps = [...prevState.savedSteps];
        
        // Type action should include the secret flag and value_mode
        if (action === "type") {
          updatedSteps[prevState.editingStepIndex] = {
            ...updatedSteps[prevState.editingStepIndex], // Preserve existing properties like id
            action,
            value,
            element,
            secret: secret, // Include secret flag for type actions
            value_mode: value_mode, // Include value_mode for type actions
            metadata: {
              ...updatedSteps[prevState.editingStepIndex].metadata,
              ...(screenshotUrl && { step_screenshot: screenshotUrl })
            }
          };
        } else {
          updatedSteps[prevState.editingStepIndex] = {
            ...updatedSteps[prevState.editingStepIndex], // Preserve existing properties like id
            action,
            value,
            element,
            secret: false, // Not applicable for other actions
            metadata: {
              ...updatedSteps[prevState.editingStepIndex].metadata,
              ...(screenshotUrl && { step_screenshot: screenshotUrl })
            }
          };
        }

        return {
          ...prevState,
          savedSteps: updatedSteps,
          currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
          editingStepIndex: undefined,
        };
      });
      
      // Clear screenshot state after saving
      setStepScreenshot(null);
      setScreenshotPreview(null);
      
      // Clear screenshot state after saving
      setStepScreenshot(null);
      setScreenshotPreview(null);
    } else {
      // Handle screenshot URL for new steps
      const stepId = uuidv4();
      let screenshotUrl = null;
      
      if (stepScreenshot && (action === "type" || action === "click" || action === "select")) {
        // Check if screenshot already has an uploaded URL
        if (stepScreenshot.uploadedUrl) {
          screenshotUrl = stepScreenshot.uploadedUrl;
        } else {
          // Fallback: upload if not already uploaded
          screenshotUrl = await uploadScreenshotToSupabase(stepScreenshot, stepId);
        }
      }
      
      
      setFormData((prevState) => ({
        ...prevState,
        savedSteps: [
          ...prevState.savedSteps,
          { 
            id: null, // No temporary ID for new steps
            action, 
            value, 
            element, 
            order: nextOrder,
            secret: action === "type" ? secret : false, // Include secret flag only for type actions
            ...(action === "type" && { value_mode: value_mode }), // Include value_mode only for type actions
            ...(action === "navigate" && { value_mode: value_mode }),
                        ...(action === "assert" && { 
               assertion: value,
               assertion_mode: formData.currentStep.assertion_mode || 'flexible',
               metadata: {
                 actionType: action,
                 assertion: value,
                 assertion_mode: formData.currentStep.assertion_mode || 'flexible',
                 ...(screenshotUrl && { step_screenshot: screenshotUrl })
               }
             }),
            ...(action !== "assert" && { 
              metadata: {
                actionType: action,
                selector: element,
                value: value,
                ...(screenshotUrl && { step_screenshot: screenshotUrl })
              }
            })
          },
        ],
        currentStep: { action: "type", value: "", element: "", exact: false, secret: false, value_mode: "static" },
      }));
 
      
      // Clear screenshot state after saving
      setStepScreenshot(null);
      setScreenshotPreview(null);
    }

    setError(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveStep();
    }
  };

  const handleRemoveStep = (index) => {
    setFormData((prev) => ({
      ...prev,
      savedSteps: prev.savedSteps.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.testname.trim()) {
      alert("Please provide a test name");
      return;
    }

    if (formData.savedSteps.length === 0 && savedApiCalls.length === 0) {
      alert("Please add at least one test step or API call");
      return;
    }

    try {
      const extractReferencedStepIds = (text) => {
        if (!text) return [];
        const matches = Array.from(text.matchAll(/\{\{\s*step:([a-f0-9\-]+)\.value\s*\}\}/gi));
        const ids = matches.map(m => m[1]);
        return Array.from(new Set(ids));
      };
      // Format normal steps
      const formattedRegularSteps = formData.savedSteps.map((step) => {

        // Process the selector based on action type and exact flag
        const selector = step.action === 'click' && step.exact 
          ? (step.element?.match(/\{\{([^}]+)\}\}/) ? step.element.match(/\{\{([^}]+)\}\}/)[1].trim() : step.element)
          : (step.element || '');
          
        // Base step structure
        const formattedStep = {
          order: step.order,
          existingStepId: step.id && !step.id.toString().startsWith('temp-') ? step.id : null,
          newStepData: {
            name: formatStepName(step),
            metadata: {
              actionType: step.action,
              selector: selector,
              value: step.value,
              ...(step.action === "click" && { exact: step.exact }),
              ...(step.action === "type" && { 
                secret: step.secret,
                value_mode: step.value_mode
              }),
              ...(step.action === "navigate" && { 
                value_mode: step.value_mode
              }),
                            ...(step.action === "assert" && { 
                 assertion: step.value,
                 assertion_mode: step.assertion_mode || 'flexible',
                 ...(() => {
                   const refIds = extractReferencedStepIds(step.value);
                   return refIds.length > 0 ? { referenced_step_id: refIds[0] } : {};
                 })()
               }),
              ...(step.action === "wait" && { 
                value: step.value // Wait value is already in milliseconds
              }),
              ...(step.action === "reload" && {
              }),
              // Include screenshot URL if available in step metadata
              ...(step.metadata?.step_screenshot && { step_screenshot: step.metadata.step_screenshot })
            },
          },
          subWorkflowId: null,
        };


        // For workflow steps, add the workflow ID
        if (step.action === "workflow" && step.workflowId) {
          formattedStep.newStepData.metadata = {
            workflow_name: step.workflowName,
          };
          formattedStep.subWorkflowId = step.workflowId;
        }

        return formattedStep;
      });

      // Format API call steps
      const formattedApiCallSteps = savedApiCalls.map(call => ({
        order: call.order || 0,
        existingStepId: null,
        newStepData: {
          name: `API Call: ${call.name}`,
          metadata: {
            actionType: 'apicall',
            endpoint_id: call.id
          }
        },
        subWorkflowId: null
      }));

      // Combine all steps and ensure sequential ordering
      const finalSteps = [...formattedRegularSteps, ...formattedApiCallSteps]
        .sort((a, b) => a.order - b.order)
        .map((step, index) => ({
          ...step,
          order: index + 1
        }));

      const testCaseData = {
        testname: formData.testname,
        description: formData.description || "",
        assertion: formData.assertion || "",
        is_disabled: formData.is_disabled || false,
        steps: finalSteps,
      };


      await testcaseApi.editTestCase(testCaseId, testCaseData);
      
      // Clear screenshot state after successful save
      setStepScreenshot(null);
      setScreenshotPreview(null);
      
      navigate(-1); // Go back to previous page
    } catch (error) {
      console.error("Error updating test case:", error);
      setError("Failed to update test case");
    }
  };

  // Helper function to create a human-readable step name for display only
  const formatStepNameForDisplay = (step) => {
    switch (step.action) {
      case "type":
        // Mask the value if the element is a password field or if secret is true
        const shouldMaskValue = step.element?.toLowerCase().includes('password') || step.secret === true;
        const displayValue = shouldMaskValue ? '••••••••' : step.value;
        
        return `Type "${displayValue}" into ${step.element}`;
      case "click":
        // For click steps, wrap the element in brackets if exact matching is enabled
        const displayElement = step.exact ? 
          `{{${step.element.replace(/^\{\{|\}\}$/g, '')}}}` : 
          step.element;
        return `Click on ${displayElement}`;
      case "navigate":
        let navModeIndicator = "";
        if (step.value_mode === "fixture") {
          navModeIndicator = " (fixture)";
        }
        return `Navigate to ${step.value}${navModeIndicator}`;
      case "select":
        return `Select "${step.value}" from "${step.element}"`;
      case "enter":
        return `Press Enter key`;
      case "workflow":
        return `Run workflow: ${step.workflowName}`;
      case "assert":
        return `Assert: ${step.value}`;
      case "wait":
        // Convert milliseconds back to seconds for display
        const seconds = parseFloat(step.value) / 1000;
        return `Wait for ${seconds} second${seconds !== 1 ? 's' : ''}`;
      case "reload":
        return `Reload page`;
      default:
        return `${step.action} ${step.element}`;
    }
  };

  // Original formatStepName function without masking for API use
  const formatStepName = (step) => {
    switch (step.action) {
      case "type":
        // Here we don't mask the value even for password fields since this is for API internal use
        let valueModeIndicator = "";
        if (step.value_mode === "fixture") {
          valueModeIndicator = " (fixture)";
        } else if (step.value_mode === "random") {
          valueModeIndicator = " (random)";
        }
        return `Type "${step.value}" into ${step.element}`;
      case "click":
        // For click steps, wrap the element in brackets if exact matching is enabled
        const displayElement = step.exact ? 
          `{{${step.element.replace(/^\{\{|\}\}$/g, '')}}}` : 
          step.element;
        return `Click on ${displayElement}`;
      case "navigate":
        let navModeIndicator = "";
        if (step.value_mode === "fixture") {
          navModeIndicator = " (fixture)";
        }
        return `Navigate to ${step.value}${navModeIndicator}`;
      case "select":
        return `Select "${step.value}" from "${step.element}"`;
      case "enter":
        return `Press Enter key`;
      case "workflow":
        return `Run workflow: ${step.workflowName}`;
      case "assert":
        return `Assert: ${step.value}`;
      case "wait":
        // Convert milliseconds to seconds for display
        const seconds = parseFloat(step.value) / 1000;
        return `Wait for ${seconds} second${seconds !== 1 ? 's' : ''}`;
      case "reload":
        return `Reload page`;
      default:
        return `${step.action} ${step.element}`;
    }
  };

  // Add this function to handle editing a step
  const handleEditStep = (index) => {
    // Get the step we're editing
    const stepToEdit = { ...formData.savedSteps[index] };
    
    console.log("Editing step:", stepToEdit); // Debug log
    
    // For click actions, check if the exact property is set and wrap element in brackets if needed
    if (stepToEdit.action === 'click' && stepToEdit.exact) {
      // If element doesn't already have brackets, add them
      if (!stepToEdit.element.match(/^\{\{.*\}\}$/)) {
        stepToEdit.element = `{{${stepToEdit.element}}}`;
      }
    }
    
    // Ensure value_mode is set for type actions
    if (stepToEdit.action === 'type' && !stepToEdit.value_mode) {
      stepToEdit.value_mode = 'static';
    }
    
    // Ensure navigate actions have value_mode set
    if (stepToEdit.action === 'navigate' && !stepToEdit.value_mode) {
      stepToEdit.value_mode = 'static';
    }
    
    // For wait actions, convert milliseconds back to seconds for editing
    if (stepToEdit.action === 'wait') {
      stepToEdit.value = parseFloat(stepToEdit.value) / 1000; // Convert ms string to seconds
    }
    
    // Set the current step to the selected saved step
    setFormData((prev) => {
      const updatedState = {
        ...prev,
        currentStep: stepToEdit,
        editingStepIndex: index, // Track which step is being edited
      };
      console.log("Updated form state:", updatedState); // Debug log
      return updatedState;
    });
  };

  // Add this function to handle drag end events
  const handleDragEnd = (result) => {
    // If dropped outside the list or no movement, do nothing
    if (
      !result.destination ||
      result.destination.index === result.source.index
    ) {
      return;
    }

    // Create a copy of the saved steps
    const newSteps = Array.from(formData.savedSteps);

    // Remove the dragged item from its original position
    const [movedItem] = newSteps.splice(result.source.index, 1);

    // Insert the dragged item at its new position
    newSteps.splice(result.destination.index, 0, movedItem);

    // Update the order property for each step
    const reorderedSteps = newSteps.map((step, index) => ({
      ...step,
      order: index + 1,
    }));

    // Update the state with the new order
    setFormData((prev) => ({
      ...prev,
      savedSteps: reorderedSteps,
    }));
  };

  useEffect(() => {
    const initializeBrowserSession = async () => {
      try {
        const result = await apiEndpoints.startBrowserSession(suiteId);
        setSessionId(result.sessionId);

        if (result.screenshot?.data) {
             setInitialScreenshot(`data:image/jpeg;base64,${result.screenshot.data}`);
        }
      } catch (error) {
        console.error("Failed to initialize browser session:", error);
        setError("Failed to start browser session: " + error.message);
      }
    };

    if (suiteId) {
      initializeBrowserSession();
    }
  }, [suiteId]);

  const handleResetScreenshot = async () => {
    try {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsPolling(false);
      setTestStatus(null);

      const result = await apiEndpoints.startBrowserSession(suiteId);
      setSessionId(result.sessionId);

      if (result.screenshot?.data) {
        setInitialScreenshot(`data:image/jpeg;base64,${result.screenshot.data}`);
        setScreenshots([])
      }
    } catch (error) {
      console.error("Failed to reset browser session:", error);
      setError("Failed to reset browser session: " + error.message);
    }
  };

  const startBatchStepsPolling = () => {
    if (isPolling) return;
  
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  
    setIsPolling(true);
    
    setScreenshots([]); // Clear previous screenshots

    // Store the current step index to track progress
    let currentStepIndex = 0;
  
    pollIntervalRef.current = setInterval(async () => {
      try {
        const result = await apiEndpoints.getBatchStepsResult(sessionId);
        const { status, screenshot, current_step } = result;
  
        if (
          status === "completed" ||
          status === "error" ||
          status === "failed"
        ) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setIsPolling(false);
          setTestStatus({
            status,
            description:
              status === "error"
                ? result.error || "Steps failed"
                : "All steps completed successfully",
          });
  
          if (screenshot?.data) {
            // Add final screenshot if it's new
            if (current_step > currentStepIndex) {
              setScreenshots((prev) => [...prev, `data:image/jpeg;base64,${screenshot.data}`]);
            } else {
              // Update the last screenshot
              setScreenshots((prev) => {
                const newScreenshots = [...prev];
                if (newScreenshots.length > 0) {
                  newScreenshots[newScreenshots.length - 1] = `data:image/jpeg;base64,${screenshot.data}`;
                } else {
                  newScreenshots.push(`data:image/jpeg;base60,${screenshot.data}`);
                }
                return newScreenshots;
              });
            }
          }
        } else if (status === "running" && screenshot?.data) {
          // Check if we've moved to a new step
          if (current_step && current_step > currentStepIndex) {
            // We're on a new step, add a new screenshot
            setScreenshots((prev) => [...prev, `data:image/jpeg;base64,${screenshot.data}`]);
            currentStepIndex = current_step;
          } else {
            // We're still on the same step, update the last screenshot
            setScreenshots((prev) => {
              const newScreenshots = [...prev];
              if (newScreenshots.length > 0) {
                newScreenshots[newScreenshots.length - 1] = `data:image/jpeg;base64,${screenshot.data}`;
              } else {
                newScreenshots.push(`data:image/jpeg;base64,${screenshot.data}`);
              }
              return newScreenshots;
            });
          }
        }
      } catch (error) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setIsPolling(false);
        setError("Failed to get batch steps result: " + error.message);
      }
    }, 3000);
  };

  const handleGenerateAssertion = async (e) => {
    e.preventDefault();
    setIsAssertionGenerating(true);

    const formattedSteps = formData.savedSteps.map((step) => {
      if (step.action === "workflow") {
        return {
          action: step.action,
          workflowId: step.workflowId,
        };
      }

      if (step.action === "click") {
        return {
          action: step.action,
          element: step.element || undefined,
          exact: step.exact || false, // Ensure exact is always a boolean
        };
      }

      // For other actions (type, navigate, select, enter)
      return {
        action: step.action,
        element: step.element || undefined,
        value: step.value || undefined,
        ...(step.action === "type" && { secret: step.secret }) // Include secret flag for type actions
      };
    });

   const screenshotsToUse = screenshots.length < formattedSteps.length?[]:screenshots.length > 3 ? screenshots.slice(-3) : screenshots;
   
   
   const data = await apiEndpoints.generateAssertion(formData.testname,formattedSteps,screenshotsToUse);


   if(data.status === "success") {
    setGeneratedAssertions(data.assertions);
   }
    setIsAssertionGenerating(false);
  }

  const handleAcceptAssertion = (assertion) => {
    setFormData((prev) => ({
      ...prev,
      assertion: assertion,
    }));
  }
  
  const handleGenerateSteps = async () => {
    try {
      setIsGeneratingSteps(true);
      setError(null);

      // Get the latest screenshot data
      const currentScreenshot = screenshots.length > 0 
        ? screenshots[screenshots.length - 1] 
        : initialScreenshot;

      // Remove the data:image/jpeg;base64, prefix if it exists
      const screenshotData = currentScreenshot?.replace(/^data:image\/jpeg;base64,/, '');

      const response = await apiEndpoints.generateSteps(formData.testname, screenshotData);
      
      if (response.status === "success" && response.steps) {
        // Convert API response format to our pending steps format
        const formattedSteps = response.steps.map(step => ({
          action: step.actionType,
          value: step.value || '',
          element: step.selector || '',
          exact: step.exact || false,
          status: 'pending'
        }));
        
        setPendingSteps(formattedSteps);
        setShowGeneratedSteps(true);
      }
    } catch (error) {
      console.error('Error generating steps:', error);
      setError('Failed to generate steps: ' + error.message);
    } finally {
      setIsGeneratingSteps(false);
    }
  };

  const handleAcceptSteps = () => {
    // Get the current number of existing steps
    const currentStepsCount = formData.savedSteps.length;
    
    // Convert pending steps to saved steps and append to existing steps
    const formattedSteps = pendingSteps.map((step, index) => ({
      action: step.action,
      value: step.value || '',
      element: step.element || '',
      exact: false,
      secret: step.action === 'type' && step.element.toLowerCase().includes('password'),
      order: currentStepsCount + index + 1
    }));
    
    setFormData(prev => ({
      ...prev,
      savedSteps: [...prev.savedSteps, ...formattedSteps]
    }));
    
    // Clear pending steps and hide generated steps section
    setPendingSteps([]);
    setShowGeneratedSteps(false);
  };

  const handleRejectSteps = () => {
    // Clear pending steps and hide generated steps section
    setPendingSteps([]);
    setShowGeneratedSteps(false);
  };

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const handleRunCurrentSteps = async () => {
    if (isPolling) {
      try {
        await apiEndpoints.cancelBrowserSession(sessionId);
        
        // Clear polling interval if it exists
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        
        setIsPolling(false);
        
        // Set final status
        setTestStatus({
          status: "completed",
          description: "Test execution cancelled by user"
        });
        
      } catch (error) {
        console.error("Error cancelling test execution:", error);
        setError(`Failed to cancel test execution: ${error.message}`);
      }
      return;
    }

    openFixtureModal();
  };

  const openFixtureModal = () => {
    setIsFixtureModalOpen(true);
  };

  const closeFixtureModal = () => {
    setIsFixtureModalOpen(false);
  };

  const handleFixtureSelect = (fixture) => {
    setSelectedFixture(fixture);
    runSteps(fixture?.id);
  };

  const handleRunWithoutFixture = () => {
    setSelectedFixture(null);
    runSteps();
  };

  // New function to handle the actual running of steps
  const runSteps = async (fixtureId = null) => {
    try {
      if (!sessionId) {
        setError("No active browser session");
        return;
      }

      if (formData.savedSteps.length === 0 && savedApiCalls.length === 0) {
        setError("Please add at least one step first");
        return;
      }

      setIsPolling(true);
      setTestStatus(null);

      // Format the steps for API execution
      const formattedSteps = formData.savedSteps.map(step => {
        if (step.action === 'workflow') {
          return {
            action: step.action,
            workflowId: step.workflowId
          };
        }

        if (step.action === 'click') {
          // For click actions with exact match, extract the text from within brackets
          let elementValue = step.element || '';
          
          if (step.exact) {
            const bracketMatch = elementValue.match(/\{\{([^}]+)\}\}/);
            if (bracketMatch && bracketMatch[1]) {
              elementValue = bracketMatch[1].trim();
            }
          }
          
          return {
            action: step.action,
            element: elementValue || undefined,
            exact: step.exact || false,  // Ensure exact is always a boolean
            ...(step.metadata?.step_screenshot && { step_screenshot: step.metadata.step_screenshot })
          };
        }

        if (step.action === 'type') {
          return {
            action: step.action,
            element: step.element || undefined,
            value: step.value || undefined,
            secret: step.secret || false,
            value_mode: step.value_mode || "static",
            ...(step.metadata?.step_screenshot && { step_screenshot: step.metadata.step_screenshot })
          };
        }

        if (step.action === 'navigate') {
          return {
            action: step.action,
            value: step.value || undefined,
            value_mode: step.value_mode || "static",
            ...(step.metadata?.step_screenshot && { step_screenshot: step.metadata.step_screenshot })
          };
        }
        
                if (step.action === 'assert') {
           return {
             action: step.action,
             value: step.value || undefined,
             assertion: step.value || undefined,
             assertion_mode: step.assertion_mode || 'flexible',
             ...(step.metadata?.step_screenshot && { step_screenshot: step.metadata.step_screenshot })
           };
         }

        // For other actions (select, enter)
        return {
          action: step.action,
          element: step.element || undefined,
          value: step.value || undefined,
          ...(step.metadata?.step_screenshot && { step_screenshot: step.metadata.step_screenshot })
        };
      });

      // Format API call steps
      const formattedApiCallSteps = savedApiCalls.map(call => ({
        action: 'api-request',
        actionType: 'api-request',
        endpoint_id: call.id
      }));

      // Combine all steps
      const allSteps = [...formattedSteps, ...formattedApiCallSteps]
        .sort((a, b) => a.order - b.order);

      await apiEndpoints.executeBrowserSteps(
        allSteps,
        suiteId,
        sessionId,
        fixtureId
      );
      
      if (!isPolling) {
        startBatchStepsPolling();
      }
    } catch (error) {
      console.error("Error running steps:", error);
      setError(`Failed to run steps: ${error.message}`);
      setIsPolling(false); 
    }
  };

  // Add value_mode handler
  const handleValueModeChange = (mode) => {
    setFormData(prev => ({
      ...prev,
      currentStep: {
        ...prev.currentStep,
        value_mode: mode
      }
    }));
  };

  // Handler to remove existing screenshot when editing a step
  const handleRemoveExistingScreenshot = () => {
    if (formData.editingStepIndex !== undefined) {
      // We're editing a step, so update both currentStep and the step in savedSteps
      setFormData(prev => {
        const updatedSteps = [...prev.savedSteps];
        const currentStep = updatedSteps[prev.editingStepIndex];
        
        // Remove screenshot from step metadata
        if (currentStep && currentStep.metadata) {
          currentStep.metadata = {
            ...currentStep.metadata,
            step_screenshot: undefined
          };
          delete currentStep.metadata.step_screenshot;
        }
        
        // Also update the current step being edited
        const updatedCurrentStep = {
          ...prev.currentStep,
          metadata: {
            ...prev.currentStep.metadata,
            step_screenshot: undefined
          }
        };
        delete updatedCurrentStep.metadata.step_screenshot;
        
        return {
          ...prev,
          savedSteps: updatedSteps,
          currentStep: updatedCurrentStep
        };
      });
    }
  };

  // Handler for saving API calls
  const handleSaveApiCall = () => {
    // Validate the API call selection
    if (!selectedApiCallId) {
      setError('Please select an API call');
      return;
    }
    
    // Find the selected API call from available API calls
    const selectedApiCall = availableApiCalls.find(call => call.id === selectedApiCallId);
    if (!selectedApiCall) {
      setError('Selected API call not found');
      return;
    }
    
    // Check if we're editing an existing API call
    if (editingApiCallIndex !== undefined) {
      setSavedApiCalls(prev => {
        const updated = [...prev];
        updated[editingApiCallIndex] = { 
          id: selectedApiCall.id,
          name: selectedApiCall.name,
          method: selectedApiCall.method,
          url: selectedApiCall.url,
          order: updated[editingApiCallIndex].order
        };
        return updated;
      });
      
      // Reset the form
      setSelectedApiCallId('');
      setApiCall({
        name: '',
        method: 'GET',
        url: '',
        body: ''
      });
      setEditingApiCallIndex(undefined);
      setError(null);
      return;
    }
    
    // Add a new API call reference with the next order number
    const nextOrder = savedApiCalls.length + formData.savedSteps.length + 1;
    setSavedApiCalls(prev => [...prev, { 
      id: selectedApiCall.id,
      name: selectedApiCall.name,
      method: selectedApiCall.method,
      url: selectedApiCall.url,
      order: nextOrder
    }]);
    
    // Reset the form
    setSelectedApiCallId('');
    setApiCall({
      name: '',
      method: 'GET',
      url: '',
      body: ''
    });
    setError(null);
  };

  const handleEditApiCall = (index) => {
    // Get the API call to edit
    const apiCallToEdit = savedApiCalls[index];
    
    // Set the form values
    setApiCall({ 
      ...apiCallToEdit,
      // Only keep the position from the saved API call
      name: '',
      method: 'GET',
      url: '',
      body: ''
    });
    
    // Set the selected API call ID
    setSelectedApiCallId(apiCallToEdit.id);
    
    // Set editing mode
    setEditingApiCallIndex(index);
    
    // Switch to API call mode if not already
    setApiCallMode(true);
  };
  
  const handleRemoveApiCall = (index) => {
    setSavedApiCalls(prev => prev.filter((_, i) => i !== index));
  };

  // Combine steps and API calls for display
  const getCombinedStepsForDisplay = () => {
    // Create API call step objects
    const apiCallSteps = savedApiCalls.map(call => ({
      isApiCall: true,
      ...call
    }));
    
    // Create regular step objects
    const regularSteps = formData.savedSteps.map(step => ({
      isApiCall: false,
      ...step
    }));
    
    // Combine all steps and sort by order
    return [...regularSteps, ...apiCallSteps].sort((a, b) => a.order - b.order);
  };

  // Add this function to format API call name for display
  const formatApiCallNameForDisplay = (call) => {
    if (!call || !call.method || !call.name) {
      return 'API Call';
    }
    return `${call.method} ${call.name}`;
  };

  // Add this function to handle editing a step from SavedSteps
  const handleEditStepFromList = (step) => {
    const stepIndex = formData.savedSteps.findIndex(s => s.order === step.order);
    handleEditStep(stepIndex);
  };

  // Add this function to handle removing a step from SavedSteps
  const handleRemoveStepFromList = (step) => {
    const stepIndex = formData.savedSteps.findIndex(s => s.order === step.order);
    handleRemoveStep(stepIndex);
  };

  // Add this function to handle editing an API call from SavedSteps
  const handleEditApiCallFromList = (apiCall) => {
    // Set the selected API call ID
    setSelectedApiCallId(apiCall.id);
    
    // Set the API call form data
    setApiCall({ 
      id: apiCall.id,
      name: apiCall.name,
      method: apiCall.method,
      url: apiCall.url
    });
    
    // Find the index of the API call in the saved list
    const apiCallIndex = savedApiCalls.findIndex(call => call.id === apiCall.id);
    
    // Set editing mode
    setEditingApiCallIndex(apiCallIndex);
    
    // Switch to API call mode if not already
    setApiCallMode(true);
  };

  // Add this function to handle removing an API call from SavedSteps
  const handleRemoveApiCallFromList = (apiCall) => {
    const apiCallIndex = savedApiCalls.findIndex(call => call.id === apiCall.id);
    handleRemoveApiCall(apiCallIndex);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <Navbar />
      <div className="container mx-auto p-6 grid grid-cols-5">
        <div className="grid grid-cols-1 col-span-2 gap-6 max-w-2xl">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={() => {
                  const hasUnsavedChanges = 
                    formData.testname !== originalData.testname || 
                    formData.is_disabled !== originalData.is_disabled ||
                    JSON.stringify(formData.savedSteps) !== JSON.stringify(originalData.savedSteps) ||
                    JSON.stringify(savedApiCalls) !== JSON.stringify(originalData.savedApiCalls);
                  
                  if (hasUnsavedChanges) {
                    if (window.confirm('You have unsaved changes. Are you sure you want to leave this page?')) {
                      navigate(-1);
                    }
                  } else {
                    navigate(-1);
                  }
                }}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                <span>Back to Test Cases</span>
              </button>
              <button
                onClick={handleSubmit}
                className="bg-black hover:bg-gray-800 text-white font-bold py-2 px-4 rounded transition-colors"
              >
                Save Changes
              </button>
            </div>
            <h1 className="text-2xl font-bold mb-6">Edit Test Case</h1>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">     
            <div className="space-y-6">
              <div>
                <label
                  htmlFor="testname"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Test Name
                </label>
                <input
                  type="text"
                  id="testname"
                  name="testname"
                  value={formData.testname}
                  onChange={handleChange}
                  className="w-full p-2 rounded border border-gray-300 focus:border-black focus:outline-none"
                />
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_disabled}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      is_disabled: e.target.checked
                    }))}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Disable this test
                  </span>
                </label>
                {formData.is_disabled && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    Test Disabled
                  </span>
                )}
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Test Steps
                  </label>
                  <button
                    onClick={handleGenerateSteps}
                    disabled={isGeneratingSteps || !formData.testname}
                    className={`${
                      isGeneratingSteps || !formData.testname
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-black hover:bg-gray-800"
                    } text-white font-medium py-2 px-4 rounded transition-colors`}
                    type="button"
                  >
                    {isGeneratingSteps ? "Generating..." : "Generate Steps"}
                  </button>
                </div>

                {/* Replace the old saved steps list with the new SavedSteps component */}
                {(formData.savedSteps.length > 0 || savedApiCalls.length > 0) && (
                  <SavedSteps
                    steps={getCombinedStepsForDisplay()}
                    onDragEnd={handleDragEnd}
                    onEditStep={handleEditStepFromList}
                    onRemoveStep={handleRemoveStepFromList}
                    onEditApiCall={handleEditApiCallFromList}
                    onRemoveApiCall={handleRemoveApiCallFromList}
                    formatStepNameForDisplay={formatStepNameForDisplay}
                    formatApiCallNameForDisplay={formatApiCallNameForDisplay}
                  />
                )}
                
                <GeneratedSteps 
                  steps={pendingSteps}
                  showGeneratedSteps={showGeneratedSteps}
                  onAccept={handleAcceptSteps}
                  onReject={handleRejectSteps}
                />

                {/* Rest of the form */}
                <TestStepForm
                  apiCallMode={apiCallMode}
                  setApiCallMode={setApiCallMode}
                  formData={formData}
                  setFormData={setFormData}
                  handleSaveStep={handleSaveStep}
                  handleToggleSecret={handleToggleSecret}
                  handleValueModeChange={handleValueModeChange}
                  handleGenerateAssertion={handleGenerateAssertion}
                  isAssertionGenerating={isAssertionGenerating}
                  genratedAssertions={genratedAssertions}
                  TEST_ACTIONS={TEST_ACTIONS}
                  availableWorkflows={availableWorkflows}
                  isLoadingApiCalls={isLoadingApiCalls}
                  apiCallsError={apiCallsError}
                  availableApiCalls={availableApiCalls}
                  selectedApiCallId={selectedApiCallId}
                  setSelectedApiCallId={setSelectedApiCallId}
                  handleSaveApiCall={handleSaveApiCall}
                  editingApiCallIndex={editingApiCallIndex}
                  setApiCall={setApiCall}
                  setEditingApiCallIndex={setEditingApiCallIndex}
                  handleKeyPress={handleKeyPress}
                  error={error}
                  stepScreenshot={stepScreenshot}
                  setStepScreenshot={setStepScreenshot}
                  screenshotPreview={screenshotPreview}
                  setScreenshotPreview={setScreenshotPreview}
                  isUploadingScreenshot={isUploadingScreenshot}
                  setIsUploadingScreenshot={setIsUploadingScreenshot}
                  existingScreenshotUrl={formData.currentStep?.metadata?.step_screenshot}
                  onRemoveExistingScreenshot={handleRemoveExistingScreenshot}
                />

                {error && <div className="text-red-600 text-sm">{error}</div>}

                <div className="flex gap-4 mt-6">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="bg-black hover:bg-gray-800 text-white font-bold py-2 px-4 rounded transition-colors"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-3">
          <ScreenshotPreview
            title="Screenshot Preview"
            isPolling={isPolling}
            screenshots={[initialScreenshot,...screenshots]}
            testStatus={testStatus}
            pollingMessage="Running test case..."
            loadingMessage="Running test steps..."
            successMessage="All test steps completed successfully."
            onReset={handleResetScreenshot}
            onRun={handleRunCurrentSteps}
            runButtonDisabled={formData.savedSteps.length === 0}
            runButtonText={isPolling ? "Cancel" : "Run from start"}
          />
        </div>
      </div>
      
      <DataFixture 
        isOpen={isFixtureModalOpen}
        onClose={closeFixtureModal}
        onSelect={handleFixtureSelect}
        onRunWithoutFixture={handleRunWithoutFixture}
      />
    </div>
  );
}

export default EditTestCase;
